/**
 * Monday transport — the wire, shared by every push kind (ARCH-026 §CRM push).
 *
 * Shared across both applications because the CRM concern spans
 * three subjects that are NOT all deals: a lead is about a request, an
 * opportunity is a computed pairing, a deal is a deal. All three speak to Monday
 * through this one client. It contains no domain logic and no column semantics: just auth, the
 * GraphQL envelope, and the config that says which board and which columns.
 *
 * INERT UNTIL CONFIGURED. With no token or board id `mondayConfigFromEnv`
 * returns null and every caller falls back to a no-op adapter, so this ships to
 * production and does nothing until the env vars are set.
 */

import { parsePointer } from "@oekopunktemarkt/entity-pointers";
import type { CrmLeadAxis } from "./lead-port";
import { assertNoOwnershipReceipt } from "./lead-admission-policy.mjs";

const MONDAY_API = "https://api.monday.com/v2";
/** Pinned so a Monday-side default bump can never silently change behaviour. */
const MONDAY_API_VERSION = "2026-04";

/** Minimal transport seam so tests never touch the network. */
export type FetchLike = (
  url: string,
  init: { method: string; headers: Record<string, string>; body: string }
) => Promise<{ ok: boolean; status: number; text(): Promise<string> }>;

/**
 * Logical field → Monday column id.
 *
 * The defaults are the ids of the live `Deals` board (5100796441). They are
 * defaults, not assumptions: every one is overridable via env, because a cloned
 * board keeps the same logical fields but mints new ids.
 */
export interface MondayColumnMap {
  dealStage?: string;
  owner?: string;
  bundesland?: string;
  plz?: string;
  ort?: string;
  naturraum?: string;
  compensationMethod?: string;
  customerRequirements?: string;
  /** Deal text column; qualification includes an explicit m² unit. */
  areaSqm?: string;
  deliveryBy?: string;
  quantity?: string;
  buyerPrice?: string;
  sellerPrice?: string;
  probability?: string;
  solution?: string;
  requestLink?: string;
  offeringLink?: string;
  /** "Total Deal Value" — the agreed price × quantity. */
  dealValue?: string;
  /** "Erwartetes Abschlussdatum" — fed from our deal deadline (ARCH-026). */
  expectedCloseDate?: string;
  /**
   * "Anfragekanal" — marks the item as machine-created (the CRM consultant's
   * recommendation over a dedicated group: filter and "group by" this column
   * and Monday renders it as a group anyway, without a second thing to
   * maintain in workflows, automations and dashboards).
   */
  channel?: string;
  /**
   * Text column holding an external key.
   *
   * ONLY used by pushes whose subject has no id of its own (an opportunity is
   * a computed pairing). A DEAL has a UUID, so its identity lives in
   * `entity_links` instead and this column is irrelevant to it.
   */
  externalKey?: string;
  /**
   * board_relation → Kontakte. Set at qualification so a deal has a human.
   *
   * NOT VERIFIED LIVE from this machine — the id below is the one recorded when
   * the boards were read on 2026-08-28 (see the plan's step 8). Every column id
   * here is env-overridable precisely so a wrong default is a config change and
   * not a deploy, and Monday rejects the WHOLE mutation on an unknown column id,
   * so the first production write is the real test.
   */
  contactRel?: string;
  /** board_relation → Unternehmen. Same provenance and caveat as `contactRel`. */
  accountRel?: string;
  /**
   * board_relation → the conversion's source Lead, the Deals side of
   * `leadColumns.dealRel`. Qualification writes and verifies the exact source;
   * related research prospects belong in their separate relation.
   */
  leadRel?: string;
  /**
   * Deals column titled `Erstkontakt` — date (with time) when the enquiry
   * entered the CRM, i.e. the deal-side copy of the lead's `Eingegangen am`.
   *
   * KEYED `receivedAt` on purpose, mirroring `leadColumns.receivedAt` which
   * holds the same instant: `leadColumns.firstContactAt` (`Erstkontakt am`)
   * means the first call BY SALES, the opposite end of the lead SLA, and a
   * second `firstContactAt` with the arrival meaning would read as the same
   * thing. The title is sales' vocabulary (Erik, 2026-09-07): on a deal the
   * clock starts where the customer relationship for THAT deal begins —
   * arrival for inbound, first contact for outreach.
   *
   * The OKR Q3 anchor (KR1 Angebotszeit, KR2 48-hour clock). Qualification
   * writes it from the lead's `Eingegangen am`, else the lead's creation time;
   * sales set it by hand on deals that never had a lead. Never overwritten.
   */
  receivedAt?: string;
  /**
   * „Achse" / „Subjekt" / „Einheit" — which obligation a deal or opportunity is
   * on, the species or landscape element where the axis carries one, and the
   * denomination its quantity is in (ARCH-039; created on the live board
   * 2026-09-17). Achse and Einheit are DROPDOWN columns: the adapters write
   * only the labels in `MONDAY_AXIS_LABELS` / `MONDAY_UNIT_LABELS`, because a
   * label the board does not carry rejects the whole mutation.
   */
  axis?: string;
  subject?: string;
  unit?: string;
  /**
   * „Deal-Art" — dropdown {Verkäufer, Käufer}: which side of the trade the
   * deal is on. Qualification fills an empty cell from the lead's `Lead-Typ`.
   */
  dealSide?: string;
}

const DEFAULT_COLUMNS: MondayColumnMap = {
  dealStage: "deal_stage",
  owner: "deal_owner",
  bundesland: "dropdown_mm5p8rht",
  plz: "text_mm5pvh7g",
  ort: "text_mm5sdvt3",
  naturraum: "text_mm5p5vec",
  compensationMethod: "dropdown_mm5pe7yz",
  customerRequirements: "dropdown_mm5pvzzk",
  areaSqm: "text_mm5pxq8y",
  deliveryBy: "text_mm5pbxmr",
  quantity: "numeric_mm5p6sqh",
  buyerPrice: "numeric_mm5pbwtp",
  sellerPrice: "numeric_mm5pzcj2",
  probability: "deal_close_probability",
  solution: "dropdown_mm5phvat",
  requestLink: "link_mm5ppfcm",
  offeringLink: "link_mm5pnewa",
  dealValue: "deal_value",
  expectedCloseDate: "deal_expected_close_date",
  channel: "color_mm5pkgca",
  // "ÖPM-Kennung" — created 2026-08-01, the last piece the opportunity push was
  // waiting on. An opportunity is a COMPUTED pairing with no id of its own, so
  // this column is the only place its `<requestId>:<offeringId>` key can live;
  // without it `findItemByExternalKey` cannot ask "is this already on the
  // board?", every press duplicated the item, and the deal push could not find
  // the opportunity card to mark as converted. Round-trip verified live:
  // `items_page_by_column_values` finds an item by a value written here.
  externalKey: "text_mm5tsxg0",
  contactRel: "deal_contact",
  accountRel: "board_relation_mm5swvqy",
  // Read from the captured Deals board schema (`move-columns.json`).
  leadRel: "board_relation_mm6nbjw4",
  // Created 2026-09-07 with this readable id (OKR Q3, plan step 2), replacing
  // the empty text column „Erstkontakt" that a lead move had leaked onto the
  // board. Read back from the live board after creation.
  receivedAt: "date_erstkontakt",
  // Read back from the live board on 2026-09-17, the day sales created them.
  axis: "dropdown_mm796epb",
  subject: "text_mm79c0jq",
  unit: "dropdown_mm792bp1",
  // Read back from the live board on 2026-09-30; created there on 2026-09-29.
  dealSide: "dropdown_mm7n3rje",
};

/** The Deals board's „Achse" dropdown labels, keyed by `compensation_axes.key`. */
export const MONDAY_AXIS_LABELS: Readonly<Record<string, string>> = {
  naturschutz: "Naturschutz",
  forst: "Forst",
  artenschutz: "Artenschutz",
  biotop: "Biotop",
};

/** The Deals board's „Einheit" dropdown labels, keyed by `axis_units.unit_key`. */
export const MONDAY_UNIT_LABELS: Readonly<Record<string, string>> = {
  oekopunkte: "Ökopunkte",
  wertpunkte: "Wertpunkte",
  ha: "ha",
  objekte: "Objekte",
  lfd_m: "lfd. m",
  stueck: "Stück",
};

/**
 * „Achse" / „Einheit" / „Subjekt" cell values for axis lines, as every board
 * with these columns takes them: dropdowns carry only labels the board knows
 * (an unknown one rejects the whole mutation), subjects are joined as text.
 * Null where there is nothing to write.
 */
export function axisCells(axes: CrmLeadAxis[]): {
  axis: { labels: string[] } | null;
  unit: { labels: string[] } | null;
  subject: string | null;
} {
  const labels = (keys: (string | null)[], vocabulary: Readonly<Record<string, string>>) => {
    const known = [
      ...new Set(keys.flatMap((key) => (key && vocabulary[key] ? [vocabulary[key]] : []))),
    ];
    return known.length ? { labels: known } : null;
  };
  const subjects = [...new Set(axes.flatMap((a) => (a.subjectLabel ? [a.subjectLabel] : [])))];
  return {
    axis: labels(
      axes.map((a) => a.axisKey),
      MONDAY_AXIS_LABELS
    ),
    unit: labels(
      axes.map((a) => a.unitKey),
      MONDAY_UNIT_LABELS
    ),
    subject: subjects.length ? subjects.join(" · ") : null,
  };
}

const COLUMN_ENV: Record<keyof MondayColumnMap, string> = {
  dealStage: "MONDAY_COL_DEAL_STAGE",
  owner: "MONDAY_COL_OWNER",
  bundesland: "MONDAY_COL_BUNDESLAND",
  plz: "MONDAY_COL_PLZ",
  ort: "MONDAY_COL_ORT",
  naturraum: "MONDAY_COL_NATURRAUM",
  compensationMethod: "MONDAY_COL_COMPENSATION_METHOD",
  customerRequirements: "MONDAY_COL_CUSTOMER_REQUIREMENTS",
  areaSqm: "MONDAY_COL_AREA",
  deliveryBy: "MONDAY_COL_DELIVERY",
  quantity: "MONDAY_COL_QUANTITY",
  buyerPrice: "MONDAY_COL_BUYER_PRICE",
  sellerPrice: "MONDAY_COL_SELLER_PRICE",
  probability: "MONDAY_COL_PROBABILITY",
  solution: "MONDAY_COL_SOLUTION",
  requestLink: "MONDAY_COL_REQUEST_LINK",
  offeringLink: "MONDAY_COL_OFFERING_LINK",
  dealValue: "MONDAY_COL_DEAL_VALUE",
  expectedCloseDate: "MONDAY_COL_EXPECTED_CLOSE",
  channel: "MONDAY_COL_CHANNEL",
  externalKey: "MONDAY_COL_EXTERNAL_KEY",
  contactRel: "MONDAY_COL_DEAL_CONTACT",
  accountRel: "MONDAY_COL_DEAL_ACCOUNT",
  leadRel: "MONDAY_COL_DEAL_LEADS",
  receivedAt: "MONDAY_COL_RECEIVED_AT",
  axis: "MONDAY_COL_AXIS",
  subject: "MONDAY_COL_SUBJECT",
  unit: "MONDAY_COL_UNIT",
  dealSide: "MONDAY_COL_DEAL_SIDE",
};

/**
 * Logical field → column id on the LEADS board (5100796443).
 *
 * The `personKey` … `dealRel` block was added for ARCH-040 (four inbound
 * sources, one deduplicated card). All of those ids were minted on 2026-08-28
 * and read back from the live board — none is a guess.
 *
 * `company` is unchanged and deliberately so: the column was RENAMED
 * „Unternehmen" → „Firma (Angabe Kunde)" to free the title for the new
 * board_relation, but its id `lead_company` never moved. Free text holds what
 * the customer typed; the relation holds the account a human confirmed.
 */
export interface MondayLeadColumnMap {
  /**
   * „Achse" / „Subjekt" / „Einheit" — the compensation axes a lead asks about,
   * the species or elements, and the units (ARCH-039), like the Deals board's
   * columns. Multi-select dropdowns (Achse, Einheit) and text (Subjekt).
   * CONFIGURED ONLY, no defaults: a column id Monday does not know rejects the
   * whole mutation, so these stay off until the columns exist on the board.
   */
  axis?: string;
  subject?: string;
  unit?: string;
  owner?: string;
  compensationMethod?: string;
  customerRequirements?: string;
  /** Canonical source URI; configured after provisioning a text column on Leads. */
  sourceKey?: string;
  company?: string;
  email?: string;
  phone?: string;
  /**
   * "Telefon" — the board's SECOND phone column. ÖPM writes `phone` (Mobil);
   * sales types into this one. On 2026-09-02, 34 of 38 outbound leads carried a
   * number ONLY here, invisible to a reader of `phone` alone.
   */
  phoneAlt?: string;
  bundesland?: string;
  plz?: string;
  ort?: string;
  naturraum?: string;
  quantity?: string;
  areaSqm?: string;
  deliveryBy?: string;
  buyerPrice?: string;
  /** `user:<uuid>` | `mail:<addr>` | `phone:<E.164>` — the dedupe key. */
  personKey?: string;
  /** Anfrage / Angebot / Rückruf / Kontaktformular / LandVest-Analyse / Outbound. */
  leadType?: string;
  kampagne?: string;
  /** SLA start. */
  receivedAt?: string;
  /** SLA stop — set by sales, never by us. */
  firstContactAt?: string;
  /**
   * „Leadquelle" — WHERE the lead came from, as opposed to what it is.
   *
   * Pre-existing and sales-owned: its labels are acquisition channels
   * (E-Mail · LinkedIn · Google Ads · Facebook Ads · Marktrecherche 2024/25 ·
   * monday Form). Inbound platform leads had none of them, so the column sat
   * empty on every card ÖPM created — see `leadSourceLabel`.
   */
  leadSource?: string;
  /** board_relation → Unternehmen. Sales-owned; we only ever READ it. */
  unternehmenRel?: string;
  /** board_relation → Kontakte, written back at qualification. */
  contactRel?: string;
  /** board_relation → Deals, written back at qualification. */
  dealRel?: string;
  /**
   * The board's own lifecycle column. READ ONLY — sales owns it, and the
   * adapter must never write it. The lead push reads it to tell an OPEN card
   * from a closed one when deduping a person who already has several.
   */
  status?: string;
}

const DEFAULT_LEAD_COLUMNS: MondayLeadColumnMap = {
  owner: "lead_owner",
  compensationMethod: "dropdown_mm5p40tg",
  customerRequirements: "dropdown_mm5p5z95",
  company: "lead_company",
  email: "lead_email",
  phone: "lead_phone",
  bundesland: "dropdown_mm5pmf8d",
  plz: "text_mm5pd809",
  ort: "text_mm5sxzv1",
  naturraum: "text_mm5psjf7",
  quantity: "numeric_mm5ptvpy",
  areaSqm: "numeric_mm5pvt7q",
  deliveryBy: "text_mm5p8wy5",
  buyerPrice: "numeric_mm5pyfjt",
  phoneAlt: "phone_mm5s469z",
  personKey: "text_mm6nw1at",
  leadType: "color_mm6nsbn0",
  kampagne: "dropdown_mm6nhqe3",
  receivedAt: "date_mm6ne80p",
  firstContactAt: "date_mm6n33na",
  leadSource: "color_mm1gpwp8",
  unternehmenRel: "board_relation_mm6ndsvv",
  contactRel: "board_relation_mm6nanm6",
  dealRel: "board_relation_mm6n806k",
  status: "lead_status",
};

const LEAD_COLUMN_ENV: Record<keyof MondayLeadColumnMap, string> = {
  owner: "MONDAY_LEAD_COL_OWNER",
  compensationMethod: "MONDAY_LEAD_COL_COMPENSATION_METHOD",
  customerRequirements: "MONDAY_LEAD_COL_CUSTOMER_REQUIREMENTS",
  sourceKey: "MONDAY_LEAD_COL_SOURCE_KEY",
  company: "MONDAY_LEAD_COL_COMPANY",
  email: "MONDAY_LEAD_COL_EMAIL",
  phone: "MONDAY_LEAD_COL_PHONE",
  bundesland: "MONDAY_LEAD_COL_BUNDESLAND",
  plz: "MONDAY_LEAD_COL_PLZ",
  ort: "MONDAY_LEAD_COL_ORT",
  naturraum: "MONDAY_LEAD_COL_NATURRAUM",
  quantity: "MONDAY_LEAD_COL_QUANTITY",
  areaSqm: "MONDAY_LEAD_COL_AREA",
  deliveryBy: "MONDAY_LEAD_COL_DELIVERY",
  buyerPrice: "MONDAY_LEAD_COL_BUYER_PRICE",
  phoneAlt: "MONDAY_LEAD_COL_PHONE_ALT",
  personKey: "MONDAY_LEAD_COL_PERSON_KEY",
  leadType: "MONDAY_LEAD_COL_LEAD_TYPE",
  kampagne: "MONDAY_LEAD_COL_KAMPAGNE",
  receivedAt: "MONDAY_LEAD_COL_RECEIVED_AT",
  firstContactAt: "MONDAY_LEAD_COL_FIRST_CONTACT_AT",
  leadSource: "MONDAY_LEAD_COL_SOURCE",
  unternehmenRel: "MONDAY_LEAD_COL_UNTERNEHMEN_REL",
  contactRel: "MONDAY_LEAD_COL_CONTACT_REL",
  dealRel: "MONDAY_LEAD_COL_DEAL_REL",
  status: "MONDAY_LEAD_COL_STATUS",
  axis: "MONDAY_LEAD_COL_AXIS",
  subject: "MONDAY_LEAD_COL_SUBJECT",
  unit: "MONDAY_LEAD_COL_UNIT",
};

/**
 * The Kontakte board and the column carrying OUR user id.
 *
 * `Kontakt-ID (ÖPM)` already holds `users.id` for every contact bulk-imported
 * from ÖPM, which makes "do we already know this person?" an EXACT uuid match —
 * strictly better than Monday's own dedupe, which compares email/company text.
 */
export interface MondayContactsConfig {
  boardId: string;
  opmUserIdColumn: string;
  /**
   * The columns ÖPM owns on Kontakte (ARCH-040 §Ownership).
   *
   * `Unternehmen` is DELIBERATELY ABSENT. It is a board_relation and ÖPM holds
   * only `users.company_name`, a free-text string; resolving one to the other is
   * fuzzy matching, and that same rule already put „David Smith" on „Test A
   * GmbH". ÖPM writes the customer's literal answer into `firma` and a human
   * sets the relation. Same reason `rolle` and `lifecycle`'s later stages are
   * absent — those are sales' interpretation, not our data.
   */
  columns: MondayContactsColumnMap;
}

/** Logical field → column id on the KONTAKTE board (5100796442). */
export interface MondayContactsColumnMap {
  email?: string;
  phone?: string;
  mobile?: string;
  firstName?: string;
  lastName?: string;
  /** Free text: the company name as the customer typed it. NOT the relation. */
  firma?: string;
  lifecycle?: string;
  typ?: string;
  herkunft?: string;
  /** Sales' own correction field — we never write it, only read it. */
  notiz?: string;
  /**
   * board_relation → Unternehmen.
   *
   * Written ONLY at qualification, and only on an EXACT normalised-name match.
   * Never fuzzy: that rule already put „David Smith" on „Test A GmbH". Absent
   * from the profile sync's field map on purpose — see `contacts-service`.
   */
  account?: string;
}

const DEFAULT_CONTACTS_COLUMNS: MondayContactsColumnMap = {
  email: "contact_email",
  phone: "phone_mm5s44n2",
  mobile: "contact_phone",
  firstName: "text_mm5raa7z",
  lastName: "text_mm5rx6f2",
  firma: "text_mm6n1atg",
  lifecycle: "color_mm5s4xaf",
  typ: "status",
  herkunft: "text_mm5r3n8k",
  notiz: "long_text_mm6nc049",
  account: "contact_account",
};

const CONTACTS_COLUMN_ENV: Record<keyof MondayContactsColumnMap, string> = {
  email: "MONDAY_CONTACT_COL_EMAIL",
  phone: "MONDAY_CONTACT_COL_PHONE",
  mobile: "MONDAY_CONTACT_COL_MOBILE",
  firstName: "MONDAY_CONTACT_COL_FIRST_NAME",
  lastName: "MONDAY_CONTACT_COL_LAST_NAME",
  firma: "MONDAY_CONTACT_COL_FIRMA",
  lifecycle: "MONDAY_CONTACT_COL_LIFECYCLE",
  typ: "MONDAY_CONTACT_COL_TYP",
  herkunft: "MONDAY_CONTACT_COL_HERKUNFT",
  notiz: "MONDAY_CONTACT_COL_NOTIZ",
  account: "MONDAY_CONTACT_COL_ACCOUNT",
};

export interface MondayConfig {
  token: string;
  boardId: string;
  /** Leads board — a DIFFERENT board from `boardId` (deals/opportunities). */
  leadsBoardId?: string;
  leadColumns: MondayLeadColumnMap;
  contacts: MondayContactsConfig;
  /**
   * Unternehmen board — the ACCOUNT MEMORY, and the answer to "have we already
   * burned this company?".
   *
   * Read-only to ÖPM. We look a company up by exact normalised name at
   * qualification and set the relations; we never CREATE one, because deciding
   * that two spellings are the same firm is a judgement, and the fuzzy version
   * of that rule already put „David Smith" on „Test A GmbH".
   */
  companiesBoardId: string;
  /** Destination group on the Deals board for qualified Leads. */
  qualificationDealGroupId: string;
  columns: MondayColumnMap;
  /**
   * Deal-Phase an OPPORTUNITY enters at — sales has not qualified it yet.
   *
   * Opportunities and deals are the SAME Monday object (Monday CRM has no
   * Opportunity entity), separated only by pipeline position. A deal does not
   * get a config knob here: its entry phase is mapped from its own status, so
   * a materialised deal lands where it actually stands.
   */
  opportunityStageLabel: string;
  /** Value written to the "Anfragekanal" column to mark machine-created items. */
  channelLabel: string;
  /**
   * Value written to the Leads board's „Leadquelle" column.
   *
   * The label did not exist when the push was built — the column's vocabulary is
   * outbound acquisition channels — so every ÖPM-created card left it blank and
   * sales' own grouping put 100 % of inbound in an empty bucket.
   *
   * It is created on first use rather than pre-registered, and that ordering is
   * the whole reason for the design: writing an UNKNOWN status label makes
   * Monday reject the entire mutation, so a deploy that landed before somebody
   * added the label by hand would have dead-lettered every inbound lead until
   * someone noticed. See `pushLead`, which scopes `create_labels_if_missing` to
   * this one column so a typo elsewhere still fails loudly.
   */
  leadSourceLabel: string;
  /**
   * The Emails & Activities activity type an incoming e-mail is logged as
   * (the account's custom activity „E-Mail"). Configured only: without it,
   * nothing is written to the timeline.
   */
  emailActivityId?: string;
}

/**
 * CONFIG-level warnings that would otherwise repeat once per pushed item.
 *
 * The key must name a CONFIGURATION fact ("leads-board-unset"), never an item.
 * An item-scoped key (`deal-stage-<itemId>`) is unique by construction, so it
 * never dedupes anything — it only leaks one Set entry per failing item for the
 * life of a warm serverless instance. Per-item problems go to `console.warn`
 * directly; every occurrence is real information.
 */
const warned = new Set<string>();
export function warnOnce(key: string, message: string): void {
  if (warned.has(key)) return;
  warned.add(key);
  console.warn(message);
}

/**
 * Clear the once-per-process warning registry. TEST ONLY — without it a suite
 * that asserts on a warning becomes order-dependent, because an earlier test
 * tripping the same key silently swallows the later one.
 */
export function resetMondayWarningsForTest(): void {
  warned.clear();
}

/**
 * Apply `<MAP>_COLUMN_ENV` overrides onto a defaults map.
 *
 * ONE implementation for all three column maps (deals, leads, contacts). The
 * rule it encodes is subtle enough that three copies would drift: an explicitly
 * empty override DISABLES the column, so the value has to be compared AFTER
 * trimming — `MONDAY_COL_X="   "` was once read as "set but blank" by a raw
 * `=== ""` test and silently kept the default instead.
 */
function columnsFromEnv<T extends object>(
  defaults: T,
  envNames: Record<keyof T, string>,
  env: Record<string, string | undefined>
): T {
  // The maps are interfaces of all-optional string fields, which are NOT
  // assignable to an index-signature type, so the loop works through a widened
  // view. The public signature stays exact — callers get their own map type
  // back, and a typo in a key is still a compile error via `Record<keyof T,…>`.
  const columns = { ...defaults } as Record<string, string | undefined>;
  for (const [key, envName] of Object.entries(envNames) as [string, string][]) {
    const override = env[envName]?.trim();
    if (override) columns[key] = override;
    else if (override === "") delete columns[key];
  }
  return columns as T;
}

/**
 * Who is asking. On a Vercel preview only the Sandbox may be written, and only
 * for the purposes `mondayWriteGate` lets through.
 */
export type MondayConfigPurpose = "outbound" | "qualification" | "email_intake";

/**
 * The CRM boards and their column ids, without the shared API token.
 *
 * The pushes read them through `mondayConfigFromEnv`; the Monday app reads the
 * same boards with each person's own OAuth token, so it takes this half only
 * and never falls back to the shared token.
 */
export interface MondayBoards {
  /** The Deals board (`MONDAY_BOARD_ID`); without it every push stays inert. */
  boardId?: string;
  leadsBoardId?: string;
  companiesBoardId: string;
  columns: MondayColumnMap;
  leadColumns: MondayLeadColumnMap;
  contacts: MondayContactsConfig;
}

/**
 * The live CRM boards. The column defaults in this file are theirs, and so are
 * the Kontakte and Unternehmen defaults below; next to any other board they
 * name the wrong board or relation.
 */
export const MONDAY_LIVE_BOARDS = {
  deals: "5100796441",
  leads: "5100796443",
  contacts: "5100796442",
  companies: "5100796444",
} as const;

export function mondayBoardsFromEnv(
  env: Record<string, string | undefined> = process.env
): MondayBoards {
  return {
    boardId: env.MONDAY_BOARD_ID?.trim() || undefined,
    leadsBoardId: env.MONDAY_LEADS_BOARD_ID?.trim() || undefined,
    companiesBoardId: env.MONDAY_COMPANIES_BOARD_ID?.trim() || MONDAY_LIVE_BOARDS.companies,
    columns: columnsFromEnv(DEFAULT_COLUMNS, COLUMN_ENV, env),
    leadColumns: columnsFromEnv(DEFAULT_LEAD_COLUMNS, LEAD_COLUMN_ENV, env),
    contacts: {
      boardId: env.MONDAY_CONTACTS_BOARD_ID?.trim() || MONDAY_LIVE_BOARDS.contacts,
      opmUserIdColumn: env.MONDAY_CONTACT_COL_OPM_ID?.trim() || "text_mm5rxjtv",
      columns: columnsFromEnv(DEFAULT_CONTACTS_COLUMNS, CONTACTS_COLUMN_ENV, env),
    },
  };
}

/**
 * The Sandbox boards (workspace 7677442), read back on 2026-09-24. On a preview
 * they are the ONLY boards Monday may be written on: an opt-in alone must never
 * enable the production defaults in this file.
 */
const SANDBOX_BOARDS = {
  MONDAY_BOARD_ID: "5104354031",
  MONDAY_LEADS_BOARD_ID: "5104354033",
  MONDAY_CONTACTS_BOARD_ID: "5104354037",
  MONDAY_COMPANIES_BOARD_ID: "5104354044",
} as const;

/** The four boards a writer reaches, as `mondayBoardsFromEnv` resolves them. */
export type MondayBoardIds = Pick<MondayBoards, "boardId" | "leadsBoardId" | "companiesBoardId"> & {
  contacts: Pick<MondayContactsConfig, "boardId">;
};

/**
 * Whether all four boards are the Sandbox's. Kontakte and Unternehmen fall back
 * to the live boards when unset, so a partial Sandbox set is not the Sandbox.
 */
export function isMondaySandbox(boards: MondayBoardIds = mondayBoardsFromEnv()): boolean {
  return (
    boards.boardId === SANDBOX_BOARDS.MONDAY_BOARD_ID &&
    boards.leadsBoardId === SANDBOX_BOARDS.MONDAY_LEADS_BOARD_ID &&
    boards.contacts.boardId === SANDBOX_BOARDS.MONDAY_CONTACTS_BOARD_ID &&
    boards.companiesBoardId === SANDBOX_BOARDS.MONDAY_COMPANIES_BOARD_ID
  );
}

/**
 * Qualification writes relation columns, and cloning a board remints their ids,
 * so on a preview it needs the Sandbox's own; their live targets still need
 * validation before qualification writes.
 */
const SANDBOX_QUALIFICATION_OVERRIDES = [
  "MONDAY_QUALIFICATION_DEALS_GROUP_ID",
  "MONDAY_LEAD_COL_UNTERNEHMEN_REL",
  "MONDAY_LEAD_COL_CONTACT_REL",
  "MONDAY_LEAD_COL_DEAL_REL",
  "MONDAY_COL_DEAL_ACCOUNT",
];

/** Whether Monday may be written for a purpose here, and why not when it may not. */
export type MondayWriteGate = { permitted: true } | { permitted: false; reason: string };

/**
 * The preview boundary for every Monday writer. `mondayConfigFromEnv` applies
 * it; a caller that must tell "deliberately off on this preview" from
 * "misconfigured" asks it directly rather than re-deriving the rule.
 *
 * Outside a Vercel preview (production, or locally where VERCEL_ENV is unset)
 * everything is permitted. On a preview nothing is unless MONDAY_ALLOW_PREVIEW=1
 * and all four board ids are the Sandbox's. Then:
 *   - the e-mail intake may write. Its relations (Lead, Kontakt, Unternehmen)
 *     live on its own Intake board, whose Sandbox copy is a separate
 *     EMAIL_INTAKE_BOARD_ID, so it needs no relation overrides;
 *   - qualification may write once the Sandbox relation overrides are set too;
 *   - every other writer may write only with MONDAY_SANDBOX_ALL_WRITERS=1, for a
 *     deployment that runs the whole CRM flow against the Sandbox.
 * The flag never relaxes the board check: one live board id still refuses.
 */
export function mondayWriteGate(
  env: Record<string, string | undefined> = process.env,
  purpose: MondayConfigPurpose = "outbound"
): MondayWriteGate {
  if (env.VERCEL_ENV !== "preview") return { permitted: true };
  const sandboxed =
    env.MONDAY_ALLOW_PREVIEW?.trim() === "1" && isMondaySandbox(mondayBoardsFromEnv(env));
  if (!sandboxed) {
    return {
      permitted: false,
      reason: "preview CRM sync needs MONDAY_ALLOW_PREVIEW=1 and all four Sandbox board IDs",
    };
  }
  if (purpose === "qualification") {
    return SANDBOX_QUALIFICATION_OVERRIDES.every((name) => env[name]?.trim())
      ? { permitted: true }
      : {
          permitted: false,
          reason:
            "preview qualification also needs the qualification group and the Sandbox relation overrides",
        };
  }
  if (purpose === "email_intake" || env.MONDAY_SANDBOX_ALL_WRITERS?.trim() === "1") {
    return { permitted: true };
  }
  return {
    permitted: false,
    reason: "preview outbound CRM sync needs MONDAY_SANDBOX_ALL_WRITERS=1",
  };
}

export function mondayConfigFromEnv(
  env: Record<string, string | undefined> = process.env,
  purpose: MondayConfigPurpose = "outbound"
): MondayConfig | null {
  const gate = mondayWriteGate(env, purpose);
  if (!gate.permitted) {
    // Keyed by the reason, a configuration fact, so each distinct refusal is said once.
    warnOnce(`monday-preview-blocked:${gate.reason}`, `[Monday] ${gate.reason}`);
    return null;
  }

  const token = env.MONDAY_API_TOKEN?.trim();
  const boards = mondayBoardsFromEnv(env);
  if (!token || !boards.boardId) return null;

  return {
    ...boards,
    token,
    boardId: boards.boardId,
    qualificationDealGroupId: env.MONDAY_QUALIFICATION_DEALS_GROUP_ID?.trim() || "group_mm5t4fts",
    opportunityStageLabel: env.MONDAY_STAGE_OPPORTUNITY?.trim() || "1 | Anfrage",
    channelLabel: env.MONDAY_CHANNEL_LABEL?.trim() || "ÖPM",
    leadSourceLabel: env.MONDAY_LEAD_SOURCE_LABEL?.trim() || "oekopunktemarkt.de",
    emailActivityId: env.MONDAY_EMAIL_ACTIVITY_ID?.trim() || undefined,
  };
}

/**
 * A stored pointer's Monday item id, or null when there is no usable link.
 *
 * Lives here rather than in each push service because it decides whether a push
 * UPDATES or CREATES — two copies drifting means one push kind silently starts
 * duplicating items. A pointer we cannot parse (hand-edited row, a form from
 * before a change) degrades to "no link", so the next push creates a fresh item
 * instead of addressing a garbage id.
 */
export function mondayItemIdFromPointer(uri: string | null): string | null {
  if (!uri) return null;
  const parsed = parsePointer(uri);
  return parsed.entityType === "monday:item" ? parsed.id : null;
}

/** HTML-escape for update bodies, which Monday renders as rich text. */
export function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** How a Monday API call failed: an HTTP status, an unreadable body, or an error envelope. */
export type MondayApiFailure = "http" | "invalid-response" | "graphql" | "api";

/**
 * A failed Monday API call. The message quotes the upstream body for the push
 * jobs' diagnostics, so a caller that must not log provider payloads (the
 * Monday app, whose bodies can carry a person's token or customer data) decides
 * on `failure` and `status` and never reads the message.
 */
export class MondayApiError extends Error {
  constructor(
    readonly failure: MondayApiFailure,
    message: string,
    /** The HTTP status Monday answered with, or null when none arrived. */
    readonly status: number | null
  ) {
    super(message);
    this.name = "MondayApiError";
  }
}

export class MondayClient {
  constructor(
    private cfg: Pick<MondayConfig, "token">,
    private fetchFn: FetchLike
  ) {}

  async gql(
    query: string,
    variables: Record<string, unknown>,
    options: { idempotencyKey?: string } = {}
  ): Promise<unknown> {
    assertNoOwnershipReceipt(query, variables);
    const res = await this.fetchFn(MONDAY_API, {
      method: "POST",
      headers: {
        // Monday takes the token VERBATIM — no "Bearer " prefix.
        Authorization: this.cfg.token,
        "Content-Type": "application/json",
        "API-Version": MONDAY_API_VERSION,
        // The caller owns this operation's stable key; never reuse it across writes.
        ...(options.idempotencyKey ? { "Idempotency-Key": options.idempotencyKey } : {}),
      },
      body: JSON.stringify({ query, variables }),
    });
    return readMondayResponse(res);
  }
}

/** A file to attach to an item's file column. */
export interface MondayUpload {
  filename: string;
  mimeType: string;
  content: Uint8Array;
}

/** `FetchLike` for Monday's multipart file endpoint, whose body is form data. */
export type UploadFetchLike = (
  url: string,
  init: { method: string; headers: Record<string, string>; body: FormData }
) => Promise<{ ok: boolean; status: number; text(): Promise<string> }>;

/**
 * Attach one file to an item's file column and return Monday's asset id.
 *
 * Monday accepts uploads only on `/v2/file` as multipart form data, so this
 * cannot ride `MondayClient.gql`; it shares the token, API version and
 * response handling instead. Each call ADDS a file — a column holds many.
 */
export async function uploadFileToColumn(
  cfg: Pick<MondayConfig, "token">,
  fetchFn: UploadFetchLike,
  itemId: string,
  columnId: string,
  file: MondayUpload
): Promise<string> {
  // Interpolated into the mutation (the multipart endpoint takes only the file
  // as a variable), so both must be plain identifiers.
  if (!/^\d+$/.test(itemId) || !/^\w+$/.test(columnId)) {
    throw new Error(`Invalid Monday item ${itemId} or column ${columnId} for a file upload`);
  }
  const form = new FormData();
  form.append(
    "query",
    `mutation ($file: File!) { add_file_to_column(item_id: ${itemId}, column_id: \"${columnId}\", file: $file) { id } }`
  );
  // Copied into a plain ArrayBuffer: a Node Buffer may view shared memory,
  // which Blob does not accept.
  const blob = new Blob([Uint8Array.from(file.content)], { type: file.mimeType });
  form.append("variables[file]", blob, file.filename);
  const res = await fetchFn(`${MONDAY_API}/file`, {
    method: "POST",
    headers: { Authorization: cfg.token, "API-Version": MONDAY_API_VERSION },
    body: form,
  });
  const data = (await readMondayResponse(res)) as { add_file_to_column?: { id?: string } } | null;
  const assetId = data?.add_file_to_column?.id;
  if (!assetId) throw new Error(`Monday did not store file ${file.filename} on item ${itemId}`);
  return assetId;
}

/** One entry on an item's Emails & Activities timeline. */
export interface TimelineEntry {
  itemId: string;
  activityId: string;
  title: string;
  summary: string;
  /** HTML. */
  content: string;
  /** When it happened — where the entry sits in the timeline. */
  timestamp: string;
}

/**
 * The entry on an item's own timeline (connected items' entries excluded) with
 * this title and time, or a new one. Looked up first because a create that
 * times out on our side can still land on Monday's — a retry must find it,
 * not add a second.
 */
export async function ensureTimelineItem(
  client: MondayClient,
  entry: TimelineEntry
): Promise<string> {
  return (await findTimelineItem(client, entry)) ?? (await createTimelineItem(client, entry));
}

/**
 * Pages of an item's timeline a lookup reads (100 entries each). Beyond them
 * the lookup refuses rather than answering "not there" — that answer would
 * create a duplicate.
 */
const TIMELINE_PAGES = 20;

async function findTimelineItem(
  client: MondayClient,
  entry: Pick<TimelineEntry, "itemId" | "title" | "timestamp">
): Promise<string | null> {
  const at = Date.parse(entry.timestamp);
  let cursor: string | null = null;
  const seen = new Set<string>();
  for (let page = 0; page < TIMELINE_PAGES; page++) {
    const data = (await client.gql(
      `query($i:ID!,$c:String){ timeline(id:$i, skipConnectedItems:true){
        timeline_items_page(limit:100, cursor:$c){ cursor timeline_items { id title created_at } } } }`,
      { i: entry.itemId, c: cursor }
    )) as {
      timeline?: {
        timeline_items_page?: {
          cursor?: string | null;
          timeline_items?: { id: string; title: string | null; created_at: string }[];
        };
      };
    } | null;
    const items = data?.timeline?.timeline_items_page;
    if (!items || !Array.isArray(items.timeline_items)) {
      throw new Error(`Monday did not return the timeline of item ${entry.itemId}`);
    }
    const match = items.timeline_items.find(
      (t) => t.title === entry.title && Date.parse(t.created_at) === at
    );
    if (match) return match.id;
    cursor = items.cursor ?? null;
    if (!cursor) return null;
    if (seen.has(cursor))
      throw new Error(`Monday repeated a timeline cursor for item ${entry.itemId}`);
    seen.add(cursor);
  }
  throw new Error(
    `timeline of item ${entry.itemId} has more than ${TIMELINE_PAGES * 100} entries — refusing to treat a partial read as "not there"`
  );
}

/**
 * Monday refuses an entry whose summary is too long („Summary is too long.“, INVALID_INPUT).
 * Observed on production over the e-mail intake's first 15 e-mails (29.09.–20.10.2026): the eight
 * summaries of at most 251 characters (254 UTF-8 bytes) were accepted, the seven of 256 characters
 * (262 bytes) or more refused. That fits a limit of 255 characters as well as one of 255 bytes, so
 * the cut is at 250 bytes, which meets both.
 */
const TIMELINE_SUMMARY_MAX_BYTES = 250;
const CUT_MARK = "…";

/**
 * The summary as Monday takes it: whole when it fits, else cut at a character and marked. Only
 * the timeline's copy is cut; the lookup above matches on title and time, never on the summary.
 */
function timelineSummary(summary: string): string {
  const encoder = new TextEncoder();
  if (encoder.encode(summary).length <= TIMELINE_SUMMARY_MAX_BYTES) return summary;
  const budget = TIMELINE_SUMMARY_MAX_BYTES - encoder.encode(CUT_MARK).length;
  let cut = "";
  let bytes = 0;
  for (const char of summary) {
    bytes += encoder.encode(char).length;
    if (bytes > budget) break;
    cut += char;
  }
  return `${cut.trimEnd()}${CUT_MARK}`;
}

/** Add an entry to an item's Emails & Activities timeline and return its id. */
async function createTimelineItem(client: MondayClient, entry: TimelineEntry): Promise<string> {
  const data = (await client.gql(
    `mutation($i:ID!,$a:String!,$t:String!,$s:String,$c:String,$ts:ISO8601DateTime!){
      create_timeline_item(item_id:$i, custom_activity_id:$a, title:$t, summary:$s, content:$c, timestamp:$ts){ id }
    }`,
    {
      i: entry.itemId,
      a: entry.activityId,
      t: entry.title,
      s: timelineSummary(entry.summary),
      c: entry.content,
      ts: entry.timestamp,
    }
  )) as { create_timeline_item?: { id?: string } | null } | null;
  const id = data?.create_timeline_item?.id;
  if (!id) throw new Error(`Monday did not add the timeline entry to item ${entry.itemId}`);
  return id;
}

/**
 * The board an item sits on now, or null when Monday does not know the item.
 *
 * A stored pointer cannot answer this: a Monday move keeps the item id, and a
 * database copied between environments keeps ids from another set of boards.
 * Anything but a clean answer throws rather than reading as "absent".
 */
export async function findItemBoardId(
  client: MondayClient,
  itemId: string
): Promise<string | null> {
  const data = (await client.gql(`query ($ids: [ID!]!) { items(ids: $ids) { id board { id } } }`, {
    ids: [itemId],
  })) as { items?: { id?: string | number; board?: { id?: string | number } | null }[] } | null;
  if (!Array.isArray(data?.items)) throw new Error("Monday item lookup returned no items array");
  if (data.items.length === 0) return null;
  const item = data.items[0];
  if (data.items.length !== 1 || String(item?.id) !== itemId || !item?.board?.id) {
    throw new Error("Monday item lookup returned an unexpected item or unreadable board");
  }
  return String(item.board.id);
}

/** Move an item to another group of its board (idempotent: moving it where it is is harmless). */
export async function moveItemToGroup(
  client: MondayClient,
  itemId: string,
  groupId: string
): Promise<void> {
  const data = (await client.gql(
    `mutation($i:ID!,$g:String!){ move_item_to_group(item_id:$i, group_id:$g){ id } }`,
    { i: itemId, g: groupId }
  )) as { move_item_to_group?: { id?: string } | null } | null;
  if (!data?.move_item_to_group?.id) {
    throw new Error(`Monday did not move item ${itemId} to group ${groupId}`);
  }
}

/** The `data` of a Monday response, or a MondayApiError naming what went wrong. */
async function readMondayResponse(res: {
  ok: boolean;
  status: number;
  text(): Promise<string>;
}): Promise<unknown> {
  const text = await res.text();
  if (!res.ok)
    throw new MondayApiError(
      "http",
      `Monday HTTP ${res.status}: ${text.slice(0, 300)}`,
      res.status
    );

  let json: {
    data?: unknown;
    errors?: unknown;
    error_message?: string;
    error_code?: string;
  };
  try {
    json = JSON.parse(text);
  } catch {
    // A gateway/HTML body would otherwise throw a bare SyntaxError that says
    // nothing about which call failed.
    throw new MondayApiError(
      "invalid-response",
      `Monday returned non-JSON: ${text.slice(0, 200)}`,
      res.status
    );
  }

  // `errors` is checked for PRESENCE, not `.length` — Monday has been observed
  // to return it as an object or string, where `.length` is undefined and the
  // failure would sail through as success.
  if (json.errors !== undefined && json.errors !== null) {
    throw new MondayApiError(
      "graphql",
      `Monday GraphQL: ${JSON.stringify(json.errors).slice(0, 300)}`,
      res.status
    );
  }
  // Monday also signals auth/complexity failures with HTTP 200 and a
  // NON-GraphQL envelope that carries no `errors` at all. Treating that as
  // success made `data` undefined, which a dedupe lookup then read as
  // "not on the board" — and appended a duplicate on every throttled sweep.
  if (json.error_message) {
    throw new MondayApiError(
      "api",
      `Monday ${json.error_code ?? "error"}: ${json.error_message.slice(0, 300)}`,
      res.status
    );
  }
  return json.data;
}

/** One board item matched by a column lookup, with what the caller needs to judge it. */
export interface MondayItemMatch {
  id: string;
  name: string;
  /** The board it actually sits on — see the board guard in the lead push. */
  boardId: string | null;
  /** Text of the columns the caller asked to read back, keyed by column id. */
  columns: Record<string, string | null>;
  /** Newest update body, when requested — for change detection without a 2nd call. */
  lastUpdateBody: string | null;
}

/**
 * Items whose `columnId` cell matches ANY of `values`, as Monday orders them.
 *
 * Several values in one `column_values` array are an OR — verified live on the
 * board 2026-08-28 — which is what lets a caller ask "is this person filed under
 * any of their key forms?" in a single round trip instead of one per form.
 *
 * THE BLANK GUARD IS NOT DEFENSIVE PADDING. Also verified live: querying
 * `ÖPM-Schlüssel` with `""` does not return nothing — it returns every item
 * whose cell is EMPTY, and the first five it handed back were real leads
 * belonging to real people. A caller passing an unset key would therefore
 * "find" a stranger's card and update it with somebody else's name, email and
 * phone. Dropping blanks is the difference between no dedupe and a data leak
 * between two customers.
 *
 * `limit` defaults to 10 rather than 1 for a related reason, also verified: with
 * two items carrying the same key, `limit:1` returned the FIRST one Monday
 * happened to order — a closed card, while an open one existed. Callers that
 * need "the open one" must see all the matches and choose.
 */
export async function findItemsByColumnValue(
  client: MondayClient,
  boardId: string,
  columnId: string | undefined,
  values: string | string[],
  opts: {
    limit?: number;
    readColumns?: string[];
    withUpdates?: boolean;
    withBoard?: boolean;
    /** Follow every matching page before callers filter by status or lead type. */
    allPages?: boolean;
    /** A second column the item must ALSO match (Monday ANDs the two, verified live). */
    alsoMatching?: { columnId: string; values: string[] };
  } = {}
): Promise<MondayItemMatch[]> {
  if (!columnId || !boardId) return [];
  const wanted = (Array.isArray(values) ? values : [values]).filter((v) => v.trim());
  if (!wanted.length) return [];
  const also = opts.alsoMatching;

  // Ask for ONLY what the caller reads. The contact lookup passes neither
  // `readColumns` nor `withBoard`, and `column_values(ids: [])` returns all 32
  // columns of the item (verified live) — an unconditional selection set made
  // the cheapest query in the integration the most expensive.
  const readColumns = opts.readColumns ?? [];
  const selection = [
    "id",
    "name",
    opts.withBoard ? "board { id }" : "",
    readColumns.length ? "column_values(ids:$rc){ id text }" : "",
    opts.withUpdates ? "updates(limit:1){ body }" : "",
  ]
    .filter(Boolean)
    .join(" ");

  type ItemPage = {
    cursor?: string | null;
    items?: {
      id: string;
      name?: string;
      board?: { id?: string | number } | null;
      column_values?: { id: string; text: string | null }[];
      updates?: { body?: string }[];
    }[];
  };
  const items: NonNullable<ItemPage["items"]> = [];
  const seenCursors = new Set<string>();
  let cursor: string | null = null;

  // Monday's documented continuation for a column-value search is
  // next_items_page, preserving that search's filters in its opaque cursor:
  // https://developer.monday.com/api-reference/reference/items-page-by-column-values
  do {
    const data = (await client.gql(
      cursor
        ? `query($cursor:String!,$l:Int!${readColumns.length ? ",$rc:[String!]" : ""}){
             next_items_page(cursor:$cursor,limit:$l){ cursor items{ ${selection} } } }`
        : `query($b:ID!,$c:String!,$v:[String!]!,$l:Int!${readColumns.length ? ",$rc:[String!]" : ""}${also ? ",$c2:String!,$v2:[String!]!" : ""}){
             items_page_by_column_values(board_id:$b, limit:$l,
               columns:[{column_id:$c, column_values:$v}${also ? ",{column_id:$c2, column_values:$v2}" : ""}]){
                 cursor items{ ${selection} } } }`,
      {
        ...(cursor
          ? { cursor }
          : {
              b: boardId,
              c: columnId,
              v: wanted,
              ...(also ? { c2: also.columnId, v2: also.values } : {}),
            }),
        l: opts.limit ?? 10,
        ...(readColumns.length ? { rc: readColumns } : {}),
      }
    )) as {
      items_page_by_column_values?: ItemPage;
      next_items_page?: ItemPage;
    } | null;
    const page: ItemPage | undefined = cursor
      ? data?.next_items_page
      : data?.items_page_by_column_values;
    if (opts.allPages && !Array.isArray(page?.items)) {
      throw new Error("Monday did not return the column lookup page");
    }
    items.push(...(page?.items ?? []));
    if (!opts.allPages) break;
    cursor = page?.cursor ?? null;
    if (cursor !== null && (typeof cursor !== "string" || !cursor.trim())) {
      throw new Error("Monday returned an invalid column lookup cursor");
    }
    if (cursor && seenCursors.has(cursor)) {
      throw new Error("Monday repeated a column lookup cursor");
    }
    if (cursor) seenCursors.add(cursor);
  } while (cursor);

  return items.map((item) => ({
    id: String(item.id),
    name: item.name ?? "",
    // Coerced, not cast. GraphQL `ID` may serialise as a number, and a strict
    // `===` against the configured board id would then be false for EVERY
    // match — silently disabling the person-level dedupe with no error.
    boardId: item.board?.id != null ? String(item.board.id) : null,
    columns: Object.fromEntries((item.column_values ?? []).map((c) => [c.id, c.text ?? null])),
    lastUpdateBody: item.updates?.[0]?.body ?? null,
  }));
}
